import { Component } from '@theme/component';
import { preloadImage, isMobileBreakpoint, clamp } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';

const SWIPE_THRESHOLD = 50;
const DRAG_THRESHOLD = 30;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

/**
 * LightboxV2 - A modern lightbox with dot indicators, swipe/drag navigation, and zoom
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement[]} media - The media elements
 * @property {HTMLElement} dots - The dots container
 * @property {HTMLButtonElement} prevBtn - Previous arrow button
 * @property {HTMLButtonElement} nextBtn - Next arrow button
 *
 * @extends Component<Refs>
 */
export class LightboxV2 extends Component {
  requiredRefs = ['dialog'];

  #currentIndex = 0;
  #totalSlides = 0;
  #highResImagesLoaded = new Set();

  // Touch/drag state
  #startX = 0;
  #startY = 0;
  #currentX = 0;
  #isDragging = false;
  #hasDraggedBeyondThreshold = false;

  // Zoom state
  #scale = 1;
  #startScale = 1;
  #translateX = 0;
  #translateY = 0;
  #startTranslateX = 0;
  #startTranslateY = 0;
  #initialPinchDistance = 0;
  #isZoomed = false;

  // Double-tap detection
  #lastTapTime = 0;
  #lastTapX = 0;
  #lastTapY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#totalSlides = this.refs.media?.length || 0;
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #setupEventListeners() {
    const { dialog } = this.refs;
    if (!dialog) return;

    // Touch events for swipe/pinch
    dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    dialog.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    // Mouse events for desktop drag
    dialog.addEventListener('mousedown', this.#handleMouseDown);
    dialog.addEventListener('mousemove', this.#handleMouseMove);
    dialog.addEventListener('mouseup', this.#handleMouseUp);
    dialog.addEventListener('mouseleave', this.#handleMouseUp);

    // Click for zoom
    dialog.addEventListener('click', this.#handleClick);

    // Wheel for zoom
    dialog.addEventListener('wheel', this.#handleWheel, { passive: false });
  }

  // ==================== PUBLIC API ====================

  open(index, event) {
    if (event) event.preventDefault();

    const { dialog, media } = this.refs;
    if (!dialog || !media?.length) return;

    // Ensure index is a number
    this.#currentIndex = typeof index === 'string' ? parseInt(index, 10) : index;
    this.#totalSlides = media.length;
    this.#resetZoom();
    this.#updateActiveSlide();
    this.#updateDots();
    this.#updateArrows();

    dialog.showModal();
    this.#preloadAdjacentImages();

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#resetZoom();
    dialog.close();

    // Unlock body scroll
    document.body.style.overflow = '';

    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.goToPrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.goToNext();
        break;
    }
  }

  goToNext() {
    if (this.#isZoomed) return;
    if (this.#currentIndex < this.#totalSlides - 1) {
      this.#navigateTo(this.#currentIndex + 1);
    }
  }

  goToPrev() {
    if (this.#isZoomed) return;
    if (this.#currentIndex > 0) {
      this.#navigateTo(this.#currentIndex - 1);
    }
  }

  handleDotClick(index) {
    if (this.#isZoomed) return;
    this.#navigateTo(index);
  }

  // ==================== NAVIGATION ====================

  #navigateTo(index) {
    if (index < 0 || index >= this.#totalSlides) return;
    this.#resetZoom();
    this.#currentIndex = index;
    this.#updateActiveSlide();
    this.#updateDots();
    this.#updateArrows();
    this.#preloadAdjacentImages();
  }

  #updateActiveSlide() {
    const { media } = this.refs;
    if (!media?.length) return;

    // Carousel: translate the gallery to show current slide
    const gallery = /** @type {HTMLElement | null} */ (this.querySelector('.lbv2-gallery'));
    if (gallery) {
      gallery.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
    }

    // Force load the active image
    const activeContainer = media[this.#currentIndex];
    if (activeContainer) {
      this.#loadHighResImage(activeContainer);
    }
  }

  #updateDots() {
    const { dots } = this.refs;
    if (!dots) return;

    const dotElements = dots.querySelectorAll('.lbv2-dot');
    dotElements.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.#currentIndex);
    });
  }

  #updateArrows() {
    const { prevBtn, nextBtn } = this.refs;
    if (prevBtn) {
      prevBtn.disabled = this.#currentIndex === 0;
      prevBtn.style.opacity = this.#currentIndex === 0 ? '0.3' : '1';
    }
    if (nextBtn) {
      nextBtn.disabled = this.#currentIndex === this.#totalSlides - 1;
      nextBtn.style.opacity = this.#currentIndex === this.#totalSlides - 1 ? '0.3' : '1';
    }
  }

  #preloadAdjacentImages() {
    const { media } = this.refs;
    if (!media?.length) return;

    // Preload prev and next
    [-1, 1].forEach((offset) => {
      const idx = this.#currentIndex + offset;
      if (idx >= 0 && idx < this.#totalSlides) {
        this.#loadHighResImage(media[idx]);
      }
    });
  }

  #loadHighResImage(container) {
    if (container.dataset.mediaType !== 'image') return;

    const img = container.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    const highResUrl = img.getAttribute('data-max-resolution');
    if (!highResUrl || this.#highResImagesLoaded.has(highResUrl)) return;

    preloadImage(highResUrl);
    this.#highResImagesLoaded.add(highResUrl);

    // Optionally swap to high-res
    const newImage = new Image();
    newImage.className = img.className;
    newImage.alt = img.alt;
    newImage.setAttribute('data-max-resolution', highResUrl);
    newImage.onload = () => img.replaceWith(newImage);
    newImage.src = highResUrl;
  }

  // ==================== TOUCH HANDLING (Mobile) ====================

  #handleTouchStart = (event) => {
    const touches = event.touches;

    if (touches.length === 2) {
      // Pinch zoom start
      event.preventDefault();
      this.#initialPinchDistance = this.#getDistance(touches[0], touches[1]);
      this.#startScale = this.#scale;
      this.#isDragging = false;
    } else if (touches.length === 1) {
      const touch = touches[0];
      const now = performance.now();

      // Check for double-tap
      if (
        now - this.#lastTapTime < DOUBLE_TAP_DELAY &&
        Math.abs(touch.clientX - this.#lastTapX) < DOUBLE_TAP_DISTANCE &&
        Math.abs(touch.clientY - this.#lastTapY) < DOUBLE_TAP_DISTANCE
      ) {
        event.preventDefault();
        this.#handleDoubleTap(touch.clientX, touch.clientY);
        this.#lastTapTime = 0;
        return;
      }

      this.#lastTapTime = now;
      this.#lastTapX = touch.clientX;
      this.#lastTapY = touch.clientY;

      this.#startX = touch.clientX;
      this.#startY = touch.clientY;
      this.#currentX = 0;
      this.#isDragging = true;
      this.#hasDraggedBeyondThreshold = false;

      if (this.#isZoomed) {
        // Don't prevent default on buttons (close, arrows)
        if (!event.target.closest('button')) {
          event.preventDefault();
        }
        this.#startTranslateX = this.#translateX;
        this.#startTranslateY = this.#translateY;
      }
    }
  };

  #handleTouchMove = (event) => {
    const touches = event.touches;

    if (touches.length === 2 && this.#initialPinchDistance > 0) {
      // Pinch zoom
      event.preventDefault();
      const currentDistance = this.#getDistance(touches[0], touches[1]);
      const scaleChange = currentDistance / this.#initialPinchDistance;
      this.#scale = clamp(this.#startScale * scaleChange, MIN_ZOOM, MAX_ZOOM);
      const wasZoomed = this.#isZoomed;
      this.#isZoomed = this.#scale > 1.05;
      
      // Reset translation when zooming back to 1x to center the image
      if (wasZoomed && !this.#isZoomed) {
        this.#translateX = 0;
        this.#translateY = 0;
      }
      this.#applyZoomTransform();
    } else if (touches.length === 1 && this.#isDragging) {
      const touch = touches[0];
      const deltaX = touch.clientX - this.#startX;
      const deltaY = touch.clientY - this.#startY;

      if (this.#isZoomed) {
        // Pan while zoomed
        event.preventDefault();
        
        // Calculate max pan bounds based on zoomed image size vs viewport
        const activeSlide = this.refs.media?.[this.#currentIndex];
        const img = activeSlide?.querySelector('.lbv2-image');
        
        let newTranslateX = this.#startTranslateX + deltaX;
        let newTranslateY = this.#startTranslateY + deltaY;
        
        if (img) {
          // Get base image dimensions (before zoom transform)
          const baseWidth = img.offsetWidth;
          const baseHeight = img.offsetHeight;
          const scaledWidth = baseWidth * this.#scale;
          const scaledHeight = baseHeight * this.#scale;
          
          // Calculate how far we can pan before image edge reaches viewport edge
          // translateX/Y represent screen pixels directly
          const maxPanX = Math.max(0, (scaledWidth - window.innerWidth) / 2);
          const maxPanY = Math.max(0, (scaledHeight - window.innerHeight) / 2);
          
          // Clamp translation to keep image within bounds
          newTranslateX = clamp(newTranslateX, -maxPanX, maxPanX);
          newTranslateY = clamp(newTranslateY, -maxPanY, maxPanY);
        }
        
        this.#translateX = newTranslateX;
        this.#translateY = newTranslateY;
        this.#applyZoomTransform();
      } else {
        // Swipe to navigate
        if (Math.abs(deltaX) > DRAG_THRESHOLD) {
          this.#hasDraggedBeyondThreshold = true;
        }
        if (this.#hasDraggedBeyondThreshold) {
          event.preventDefault();
          this.#currentX = deltaX;
          this.#applySwipeTransform();
        }
      }
    }
  };

  #handleTouchEnd = (event) => {
    if (!this.#isDragging) {
      this.#initialPinchDistance = 0;
      return;
    }

    this.#isDragging = false;
    this.#initialPinchDistance = 0;

    if (!this.#isZoomed && this.#hasDraggedBeyondThreshold) {
      if (this.#currentX > SWIPE_THRESHOLD) {
        this.goToPrev();
      } else if (this.#currentX < -SWIPE_THRESHOLD) {
        this.goToNext();
      }
    }

    this.#currentX = 0;
    this.#finishSwipeTransform();
  };

  #handleDoubleTap(x, y) {
    if (this.#isZoomed) {
      this.#resetZoom();
    } else {
      this.#scale = 2;
      this.#isZoomed = true;

      // Center zoom on tap point
      const activeSlide = this.refs.media[this.#currentIndex];
      if (activeSlide) {
        const rect = activeSlide.getBoundingClientRect();
        const offsetX = x - rect.left - rect.width / 2;
        const offsetY = y - rect.top - rect.height / 2;
        this.#translateX = -offsetX;
        this.#translateY = -offsetY;
      }

      this.#applyZoomTransform();
    }
  }

  #getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ==================== MOUSE HANDLING (Desktop) ====================

  #handleMouseDown = (event) => {
    // Only handle left-click on the image area, not buttons
    if (event.button !== 0) return;
    if (event.target.closest('button')) return;

    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;

    if (this.#isZoomed) {
      this.#startTranslateX = this.#translateX;
      this.#startTranslateY = this.#translateY;
      event.preventDefault();
    }

    this.refs.dialog.style.cursor = 'grabbing';
  };

  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    const deltaX = event.clientX - this.#startX;
    const deltaY = event.clientY - this.#startY;

    if (this.#isZoomed) {
      // Pan while zoomed - clamp to image bounds
      let newTranslateX = this.#startTranslateX + deltaX;
      let newTranslateY = this.#startTranslateY + deltaY;
      
      const activeSlide = this.refs.media?.[this.#currentIndex];
      const img = activeSlide?.querySelector('.lbv2-image');
      
      if (img) {
        const baseWidth = img.offsetWidth;
        const baseHeight = img.offsetHeight;
        const scaledWidth = baseWidth * this.#scale;
        const scaledHeight = baseHeight * this.#scale;
        
        const maxPanX = Math.max(0, (scaledWidth - window.innerWidth) / 2);
        const maxPanY = Math.max(0, (scaledHeight - window.innerHeight) / 2);
        
        newTranslateX = clamp(newTranslateX, -maxPanX, maxPanX);
        newTranslateY = clamp(newTranslateY, -maxPanY, maxPanY);
      }
      
      this.#translateX = newTranslateX;
      this.#translateY = newTranslateY;
      this.#applyZoomTransform();
      
      // Mark that we panned so click doesn't trigger zoom-out
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.#hasDraggedBeyondThreshold = true;
      }
    } else {
      // Drag to navigate
      if (Math.abs(deltaX) > DRAG_THRESHOLD) {
        this.#hasDraggedBeyondThreshold = true;
      }
      if (this.#hasDraggedBeyondThreshold) {
        this.#currentX = deltaX;
        this.#applySwipeTransform();
      }
    }
  };

  #handleMouseUp = (event) => {
    if (!this.#isDragging) return;

    this.refs.dialog.style.cursor = '';
    this.#isDragging = false;

    if (!this.#isZoomed && this.#hasDraggedBeyondThreshold) {
      if (this.#currentX > DRAG_THRESHOLD) {
        this.goToPrev();
      } else if (this.#currentX < -DRAG_THRESHOLD) {
        this.goToNext();
      }
    }

    this.#currentX = 0;
    this.#finishSwipeTransform();
  };

  #handleClick = (event) => {
    // Only zoom on click if we didn't drag
    if (this.#hasDraggedBeyondThreshold) return;
    if (event.target.closest('button')) return;

    // On mobile, zoom is handled by double-tap, not single click
    if (isMobileBreakpoint()) return;

    // Only zoom when clicking directly on the image, not whitespace
    const img = event.target.closest('.lbv2-image');
    if (!img) return;

    const container = event.target.closest('.lbv2-slide');
    if (!container || container.dataset.mediaType !== 'image') return;

    if (this.#isZoomed) {
      this.#resetZoom();
    } else {
      this.#scale = 2;
      this.#isZoomed = true;

      // Center zoom on click point relative to the image
      const rect = img.getBoundingClientRect();
      const offsetX = event.clientX - rect.left - rect.width / 2;
      const offsetY = event.clientY - rect.top - rect.height / 2;
      this.#translateX = -offsetX;
      this.#translateY = -offsetY;

      this.#applyZoomTransform();
    }
  };

  #handleWheel = (event) => {
    // Prevent page scroll behind lightbox
    event.preventDefault();

    const img = event.target.closest('.lbv2-image');
    const container = event.target.closest('.lbv2-slide');
    if (!container || container.dataset.mediaType !== 'image' || !img) return;

    // Zoom in/out based on wheel direction
    const delta = event.deltaY > 0 ? -0.2 : 0.2;
    const newScale = clamp(this.#scale + delta, MIN_ZOOM, MAX_ZOOM);

    if (newScale !== this.#scale) {
      const oldScale = this.#scale;
      this.#scale = newScale;
      this.#isZoomed = this.#scale > 1;

      if (!this.#isZoomed) {
        this.#translateX = 0;
        this.#translateY = 0;
      } else {
        // Zoom toward cursor position
        const rect = img.getBoundingClientRect();
        const cursorX = event.clientX - rect.left - rect.width / 2;
        const cursorY = event.clientY - rect.top - rect.height / 2;
        
        // Adjust translation to keep cursor point stationary
        const scaleFactor = newScale / oldScale;
        this.#translateX = cursorX - (cursorX - this.#translateX) * scaleFactor;
        this.#translateY = cursorY - (cursorY - this.#translateY) * scaleFactor;
      }

      this.#applyZoomTransform();
    }
  };

  // ==================== TRANSFORMS ====================

  #applySwipeTransform() {
    const gallery = /** @type {HTMLElement | null} */ (this.querySelector('.lbv2-gallery'));
    if (!gallery) return;

    // During drag, disable transition and offset from current slide position
    gallery.style.transition = 'none';
    const baseOffset = this.#currentIndex * -100;
    const dragOffsetPercent = (this.#currentX / window.innerWidth) * 100;
    gallery.style.transform = `translateX(calc(${baseOffset}% + ${this.#currentX}px))`;
  }

  #finishSwipeTransform() {
    const gallery = /** @type {HTMLElement | null} */ (this.querySelector('.lbv2-gallery'));
    if (!gallery) return;

    // Re-enable transition and snap to current slide
    gallery.style.transition = '';
    gallery.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
  }

  #applyZoomTransform() {
    const activeSlide = this.refs.media[this.#currentIndex];
    if (!activeSlide) return;

    const img = activeSlide.querySelector('.lbv2-image');
    if (img) {
      img.style.transform = `scale(${this.#scale}) translate(${this.#translateX / this.#scale}px, ${this.#translateY / this.#scale}px)`;
      img.style.cursor = this.#isZoomed ? 'grab' : 'zoom-in';
    }
  }

  #resetZoom() {
    this.#scale = 1;
    this.#translateX = 0;
    this.#translateY = 0;
    this.#isZoomed = false;

    const { media } = this.refs;
    if (!media?.length) return;

    media.forEach((slide) => {
      const img = slide.querySelector('.lbv2-image');
      if (img) {
        img.style.transform = '';
        img.style.cursor = '';
      }
    });

    // Restore gallery to current slide position (don't clear transform)
    const gallery = /** @type {HTMLElement | null} */ (this.querySelector('.lbv2-gallery'));
    if (gallery) {
      gallery.style.transition = '';
      gallery.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
    }
  }
}

if (!customElements.get('lightbox-v2')) {
  customElements.define('lightbox-v2', LightboxV2);
}
